import { resolve, singleton } from 'aurelia';
import { HttpClient } from './http-client';

// One row of the admin users report as returned by GET /api/admin/users: a registered user
// plus their saved-search count and current notification settings. savedSearchFrequency only
// applies while notifySavedSearches is on.
export interface AdminUserReportRow {
  authSubject: string;
  email: string;
  displayName: string | null;
  tier: string;
  savedSearchCount: number;
  digestOptIn: boolean;
  notifySavedSearches: boolean;
  savedSearchFrequency: string;
  notifyAvailability: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

// Owns the admin users-report panel: loads every registered user with their saved-search count
// and notification settings so an admin can audit sign-ups and opt-ins at a glance. Mirrors the
// other singleton admin services — shared state lives here and the panel binds to it directly.
@singleton()
export class UsersReportAdminService {
  private readonly http: HttpClient = resolve(HttpClient);

  panelOpen: boolean = false;
  loading: boolean = false;
  error: string | null = null;
  rows: AdminUserReportRow[] = [];

  // Loads the report. Safe to call whenever the panel is opened; failures leave a readable
  // error and an empty list.
  async load(): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const response = await this.http.get('/api/admin/users');
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      this.rows = await response.json();
    } catch (e: any) {
      this.error = `Could not load the users report: ${e.message}`;
      this.rows = [];
    } finally {
      this.loading = false;
    }
  }

  async openPanel(): Promise<void> {
    this.panelOpen = true;
    await this.load();
  }

  closePanel(): void {
    this.panelOpen = false;
    this.error = null;
  }
}
